<script lang="ts" setup>
import {
  buildProjectMailAddress,
  checkProjectMailSlug,
  maxProjectMailSlugLength,
} from '@beeboard/common';
import { useProjectMailStore } from '@/store/projectMailStore';
import { useProjectRulesStore } from '@/store/projectRulesStore';

/**
 * The project's mail address, with its alias edited in a popup (WI #5643).
 *
 * One component because the same field is offered from two places — Edit Project and the
 * map's import sidebar — and the shared slug validator only guarantees frontend/backend
 * agreement, not agreement between copies of this form.
 *
 * Every caller stays one line high: reading the address is the common case, renaming is
 * rare, so the form lives behind a dialog.
 */
interface Props {
  projectId: string;
  /**
   * Hide the rename control. Set by the map's import sidebar, where the address is
   * context for the list rather than something to configure.
   */
  readonlyAddress?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  readonlyAddress: false,
});

const projectMailStore = useProjectMailStore();
const projectRulesStore = useProjectRulesStore();
const messages = useAlerts();

const slug = ref('');
const serverError = ref('');
const isSaving = ref(false);
const isDialogOpen = ref(false);
const isDisableDialogOpen = ref(false);
const isDisabling = ref(false);

const status = computed(() => projectMailStore.status(props.projectId));
const isEnabled = computed(() => !!status.value?.enabled);
const isMailboxConfigured = computed(() => !!status.value?.mailbox_configured);
const canManage = computed(() =>
  projectRulesStore.isProjectResponsible(props.projectId),
);
const address = computed(() => projectMailStore.address(props.projectId));
const formerSlugs = computed(() => status.value?.former_slugs ?? []);
const retentionDays = computed(() => status.value?.retention_days);

/**
 * Turning mail off stops delivery to every address this project has ever used, not just
 * the current one, and senders get no bounce — hence the confirmation.
 */
async function disable() {
  isDisableDialogOpen.value = false;
  isDisabling.value = true;

  try {
    await projectMailStore.disableAlias(props.projectId);
    isDialogOpen.value = false;
    messages.success($t('projectMail.disabled'));
  } catch (error) {
    messages.error(error);
  } finally {
    isDisabling.value = false;
  }
}

onMounted(() => void projectMailStore.ensureAliasStatus(props.projectId));

/** The same shared rule the server enforces, so the two cannot disagree. */
const slugErrors = computed(() =>
  slug.value
    ? (checkProjectMailSlug(slug.value, status.value?.mailbox_address) ?? [])
    : [],
);

const maxSlugLength = computed(() =>
  maxProjectMailSlugLength(status.value?.mailbox_address),
);

const previewAddress = computed(() => {
  const mailbox = status.value?.mailbox_address;

  return mailbox ? buildProjectMailAddress(mailbox, slug.value) : '';
});

const hasChange = computed(
  () => !!slug.value && slug.value !== status.value?.alias?.slug,
);

const canSave = computed(
  () =>
    canManage.value &&
    hasChange.value &&
    !slugErrors.value.length &&
    !isSaving.value,
);

/** Seeded on open, not on mount: the suggestion is only wanted when enabling. */
function openDialog() {
  slug.value = status.value?.alias?.slug || status.value?.suggested_slug || '';
  serverError.value = '';
  isDialogOpen.value = true;
}

async function save() {
  if (!canSave.value) {
    return;
  }

  isSaving.value = true;
  serverError.value = '';
  try {
    await projectMailStore.saveAlias(props.projectId, slug.value);
    messages.success($t('projectMail.addressSaved'));
    isDialogOpen.value = false;
  } catch (error) {
    // 409 = the slug is reserved by another project, including one that no longer
    // uses it. Surfaced rather than silently suffixed.
    serverError.value =
      (error as { status?: number })?.status === 409
        ? $t('projectMail.addressTaken')
        : '';
    if (!serverError.value) {
      messages.error(error);
    }
  } finally {
    isSaving.value = false;
  }
}

async function copyAddress() {
  if (!address.value) {
    return;
  }

  try {
    await navigator.clipboard.writeText(address.value);
    messages.success($t('projectMail.addressCopied'));
  } catch {
    messages.error($t('projectMail.addressCopyFailed'));
  }
}
</script>

<template>
  <div v-if="isMailboxConfigured" class="project-mail-alias">
    <template v-if="isEnabled">
      <code class="project-mail-alias__address" :title="address">
        {{ address }}
      </code>
      <UiButton
        variant="flat"
        size="medium"
        icon="Copy"
        :aria-label="$t('projectMail.copy')"
        :title="$t('projectMail.copy')"
        @click="copyAddress"
      />
      <UiButton
        v-if="!props.readonlyAddress"
        variant="flat"
        size="small"
        icon="Edit3"
        :disabled="!canManage"
        :aria-label="$t('button.edit')"
        :title="
          canManage ? $t('button.edit') : $t('projectMail.responsibleOnly')
        "
        @click="openDialog"
      />
    </template>

    <UiButton
      v-else
      variant="primary-text"
      size="small"
      :disabled="!canManage"
      :title="canManage ? undefined : $t('projectMail.responsibleOnly')"
      @click="openDialog"
    >
      {{ $t('projectMail.enable') }}
    </UiButton>

    <AppDialog
      v-model="isDialogOpen"
      size="small"
      :title="$t('projectMail.addressTitle')"
      :subtitle="$t('projectMail.aliasRules')"
    >
      <!-- AppDialog renders #content, not the default slot. -->
      <template #content>
        <UiInput
          v-model="slug"
          name="projectMailAliasSlug"
          :label="$t('projectMail.aliasLabel')"
          :disabled="!canManage || isSaving"
          :maxlength="maxSlugLength"
          :errors="slugErrors"
          :error="serverError"
          autofocus
        />

        <p
          v-if="previewAddress && hasChange"
          class="project-mail-alias__preview"
        >
          {{ $t('projectMail.previewLabel') }}
          <code>{{ previewAddress }}</code>
        </p>

        <div v-if="formerSlugs.length" class="project-mail-alias__former">
          <span class="project-mail-alias__former-label">
            {{ $t('projectMail.formerAddresses') }}
          </span>
          <UiTag v-for="former in formerSlugs" :key="former" type="default">
            {{ former }}
          </UiTag>
          <p class="project-mail-alias__hint">
            {{ $t('projectMail.formerAddressesHint') }}
          </p>
        </div>

        <p v-if="retentionDays" class="project-mail-alias__hint">
          {{ $t('projectMail.retentionHint', { days: retentionDays }) }}
        </p>
      </template>

      <template #actions>
        <div class="button-row project-mail-alias__actions">
          <UiButton
            v-if="canManage && isEnabled"
            variant="error-text"
            :loading="isDisabling"
            @click="isDisableDialogOpen = true"
          >
            {{ $t('projectMail.disable') }}
          </UiButton>

          <UiButton @click="isDialogOpen = false">
            {{ $t('button.cancel') }}
          </UiButton>
          <UiButton
            variant="primary"
            :disabled="!canSave"
            :loading="isSaving"
            @click="save"
          >
            {{ isEnabled ? $t('button.save') : $t('projectMail.enable') }}
          </UiButton>
        </div>
      </template>
    </AppDialog>

    <ConfirmDialog
      v-model="isDisableDialogOpen"
      :title="$t('projectMail.disableConfirmTitle')"
      :subtitle="
        $t('projectMail.disableConfirmMessage', { days: retentionDays })
      "
      @confirm="disable"
    />
  </div>
</template>

<style lang="scss" scoped>
.project-mail-alias {
  display: flex;
  align-items: center;
  gap: var(--space-4);
  min-width: 0;
}

.project-mail-alias__address {
  overflow: hidden;
  font-size: var(--font-size-13);
  color: var(--color-gray-600);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.project-mail-alias__former {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-6);
  margin-top: var(--space-12);
  font-size: var(--font-size-12);
}

.project-mail-alias__former-label {
  color: var(--color-gray-500);
}

.project-mail-alias__hint {
  margin-top: var(--space-8);
  color: var(--color-gray-500);
  font-size: var(--font-size-12);
  line-height: var(--line-height-ratio-140);
}

.project-mail-alias__preview {
  padding-top: var(--space-8);
  font-size: var(--font-size-12);
  color: var(--color-gray-500);
}

// Slot content keeps this component's scope, so the dialog's action row can be
// spaced from the field above it without reaching into AppDialog.
.button-row {
  margin-top: var(--space-20);
}
</style>
